// U4 · La fusión del carrito (PLAN_CARRITO_UNIFICADO §U4).
//
// El hallazgo del tester (22-08, doble-pestaña): 6 toques desde dos pestañas =
// 3 unidades. El POST del POS envía el carrito ENTERO según lo ve esa pestaña —
// full-replace — así que la última en guardar PISA lo de la otra. El CAS del
// servidor detecta el choque (409), pero la recuperación de entonces era
// "recarga y descarta lo mío": no perdía dinero, perdía TRABAJO.
//
// La cura es este merge A TRES BANDAS, el mismo principio que un rebase de git:
//
//   base     = la última foto del servidor que esta pestaña CONOCÍA
//   local    = lo que esta pestaña quiere guardar ahora
//   servidor = lo que hay AHORA en el servidor (lo escribió otra pestaña)
//
//   mi delta = local − base          (qué he cambiado YO desde mi última foto)
//   fusión   = servidor + mi delta   (aplico MI cambio sobre SU verdad)
//
// Así 3 toques míos + 3 toques suyos = 6, aunque ninguna pestaña viera los del
// otro. Es lógica de DINERO (el carrito se cobra): vive en un helper puro con
// sus tests — un dato, un render.
//
// Reglas finas:
// - La identidad de línea es (product_id, location_id): el mismo producto en dos
//   neveras son dos líneas (regla de oro del stock).
// - Cantidades clamp a ≥0 y las líneas a 0 desaparecen (un carrito no debe
//   "deber" unidades porque dos pestañas borraran lo mismo).
// - real_weight (báscula) y unit_price NO se suman: gana el valor LOCAL si esta
//   pestaña tocó la línea (es quien tiene la báscula delante); si no, el del
//   servidor. La merma no es aditiva entre pestañas.
// - discount_pct (el «Dto. %» de la línea) sigue la MISMA regla que la báscula:
//   gana el local si esta pestaña tocó la línea. Antes ni siquiera pasaba por
//   aquí — aMapa lo tiraba— y la fusión de un 409 le borraba al camarero el
//   descuento que acababa de teclear, con el cobro (PaymentModal) leyéndolo
//   después para mandarlo al servidor: al socio se le cobraba el precio entero.

const clave = (l) => `${String(l.product_id).toLowerCase()}|${l.location_id ? String(l.location_id).toLowerCase() : ''}`;

const aMapa = (lineas) => {
  const m = new Map();
  for (const l of lineas || []) {
    const k = clave(l);
    const prev = m.get(k);
    if (prev) {
      prev.quantity = Number((prev.quantity + (Number(l.quantity) || 0)).toFixed(3));
    } else {
      m.set(k, {
        product_id: l.product_id,
        location_id: l.location_id ?? null,
        quantity: Number(l.quantity) || 0,
        unit_price: l.unit_price != null ? Number(l.unit_price) : null,
        real_weight: l.real_weight != null ? Number(l.real_weight) : null,
        discount_pct: l.discount_pct != null ? Number(l.discount_pct) : null,
      });
    }
  }
  return m;
};

/**
 * Fusión a tres bandas. Devuelve la lista de líneas a guardar.
 * @param {Array} base     última foto del servidor que esta pestaña conocía
 * @param {Array} local    lo que esta pestaña intentó guardar
 * @param {Array} servidor lo que hay ahora en el servidor
 */
export function fusionarCarrito(base, local, servidor) {
  const mBase = aMapa(base);
  const mLocal = aMapa(local);
  const mServ = aMapa(servidor);

  const claves = new Set([...mBase.keys(), ...mLocal.keys(), ...mServ.keys()]);
  const resultado = [];

  for (const k of claves) {
    const b = mBase.get(k);
    const l = mLocal.get(k);
    const s = mServ.get(k);

    // Mi delta desde la última foto (línea nueva local = todo su qty).
    const delta = (l ? l.quantity : 0) - (b ? b.quantity : 0);
    const qty = Number(Math.max(0, (s ? s.quantity : 0) + delta).toFixed(3));
    if (qty <= 0) continue;

    // ¿Esta pestaña TOCÓ la línea? (existe en local y difiere de su base, o es nueva)
    const tocadaLocal = l != null && (b == null || l.quantity !== b.quantity ||
      l.real_weight !== (b ? b.real_weight : null) ||
      l.discount_pct !== (b ? b.discount_pct : null));

    resultado.push({
      product_id: (l || s || b).product_id,
      location_id: (l || s || b).location_id,
      quantity: qty,
      unit_price: (tocadaLocal && l?.unit_price != null) ? l.unit_price
        : (s?.unit_price ?? l?.unit_price ?? b?.unit_price ?? 0),
      real_weight: tocadaLocal ? (l?.real_weight ?? null) : (s?.real_weight ?? null),
      discount_pct: tocadaLocal ? (l?.discount_pct ?? null) : (s?.discount_pct ?? null),
    });
  }
  return resultado;
}

/**
 * Al RECONSTRUIR el carrito desde el servidor (fetchDraftCart), conservar el
 * «Dto. %» que esta pantalla ya tenía puesto. El descuento vive solo en el FE
 * mientras el backend legacy no tenga dónde guardarlo (transaction_items no
 * tiene columna; Cart v2 sí — cart_items.discount_pct, PR #486), así que un
 * refetch —el 409, re-seleccionar socio, cambiar de área— lo borraba en
 * silencio y el cobro salía por el precio entero.
 *
 * Regla: si el servidor trae un descuento (no-null), el suyo manda; si CALLA, se
 * conserva el local. La identidad de línea es la misma de la fusión:
 * (product_id, location_id).
 *
 * ## Qué significa «callar», y por qué hizo falta un tercer estado
 *
 * Esto se escribió cuando el servidor no sabía guardar el campo: `null` solo podía
 * querer decir «no tengo dónde ponerlo», así que conservar el local era lo correcto.
 *
 * Cart v2 SÍ lo guarda, y entonces `null` pasó a significar también «esta línea NO
 * tiene descuento». Con los dos sentidos en el mismo valor, quitar un descuento no
 * se podía distinguir de que el servidor no supiera del tema:
 *
 *   Tablet A pone Dto. 50% en una línea de 10 € y luego lo QUITA (se guarda null).
 *   Tablet B, que lo tenía en memoria, refresca → null → repone el 50% borrado.
 *   Se cobran 5 € en vez de 10. Pierde el club, en silencio.
 *
 * Por eso el servidor dice ahora si SABE del campo (`supports_line_discount`). Cuando
 * lo sabe, un `null` es una respuesta y no un silencio: no se repone nada.
 *
 * @param {boolean} servidorSabeDeDescuentos `supports_line_discount` de la respuesta.
 *   Por defecto `false` para que el camino legacy conserve la conducta de siempre.
 */
export function conservarDescuentosManuales(nuevas, previas, servidorSabeDeDescuentos = false) {
  // El servidor sabe guardarlo: lo que diga es la verdad, incluido el «ninguno».
  if (servidorSabeDeDescuentos) return nuevas;

  const previos = new Map();
  for (const l of previas || []) {
    if (l?.manual_discount != null) {
      previos.set(`${String(l.id).toLowerCase()}|${l.location_id ? String(l.location_id).toLowerCase() : ''}`, Number(l.manual_discount));
    }
  }
  if (previos.size === 0) return nuevas;

  return (nuevas || []).map((l) => {
    if (l?.manual_discount != null) return l; // el servidor habló: manda él
    const k = `${String(l.id).toLowerCase()}|${l.location_id ? String(l.location_id).toLowerCase() : ''}`;
    const dto = previos.get(k);
    return dto != null ? { ...l, manual_discount: dto } : l;
  });
}
